import { useMemo } from 'react';

import { RecordPreview } from '@apps/web/components/phoebe_chat_view/artifacts/record_preview/record_preview';
import { OpenRecordLink } from '@apps/web/components/phoebe_chat_view/artifacts/save_note_artifact_card';
import { useSavedNotes } from '@apps/web/components/phoebe_chat_view/artifacts/saved_note_outcomes_context';
import { ReferenceBadge } from '@apps/web/components/phoebe_chat_view/assistant_responses/reference_markdown';
import {
    type NoteOutcome,
    noteOutcomes,
} from '@apps/web/lib/agent_note_outcome_copy';
import { formatRelativeTime } from '@apps/web/lib/date_utils';
import { type AgentNoteItemRead } from '@libraries/typescript/api_client';

// Grouped by the store that changed rather than by turn: the panel answers
// "what does Phoebe know about availability" as often as "what just happened",
// and a single message routinely writes to several stores at once.
const GROUP_ORDER: ReadonlyArray<AgentNoteItemRead['domain']> = [
    'memory',
    'availability',
    'client_caregiver_preference',
    'playbook_rule',
    'outreach_filter',
    'recommendation_default',
    'agency_context',
];

// `attribute` writes a memory fragment and reads as memory to a scheduler, so
// it shares memory's bucket rather than rendering a second section with the
// same heading.
const GROUP_FOR_DOMAIN: Record<
    AgentNoteItemRead['domain'],
    AgentNoteItemRead['domain']
> = {
    memory: 'memory',
    attribute: 'memory',
    availability: 'availability',
    client_caregiver_preference: 'client_caregiver_preference',
    playbook_rule: 'playbook_rule',
    outreach_filter: 'outreach_filter',
    recommendation_default: 'recommendation_default',
    agency_context: 'agency_context',
};

const GROUP_LABELS: Record<AgentNoteItemRead['domain'], string> = {
    memory: 'Memory',
    availability: 'Availability',
    client_caregiver_preference: 'Preferences',
    attribute: 'Memory',
    playbook_rule: 'Playbook',
    outreach_filter: 'Outreach settings',
    recommendation_default: 'Matching defaults',
    agency_context: 'Agency details',
};

type Change = {
    readonly outcome: NoteOutcome;
    readonly detail: string;
    readonly at: string;
};

function ChangeRow({ change }: { readonly change: Change }) {
    const { outcome } = change;
    return (
        <li className="border-border/40 flex flex-col gap-1 border-b pb-2.5 last:border-b-0 last:pb-0">
            <div className="flex min-w-0 items-center gap-1.5">
                {outcome.entity ? (
                    <ReferenceBadge
                        reference={{
                            type: outcome.entity.type,
                            id: outcome.entity.id,
                            display_name: outcome.entity.name,
                        }}
                    />
                ) : (
                    // Org-wide changes have no person to name, so the row says
                    // what it is rather than leaving a timestamp floating alone.
                    <span className="text-muted-foreground truncate text-[11px] leading-4">
                        {outcome.text}
                    </span>
                )}
                <div className="ml-auto flex shrink-0 items-center gap-2">
                    <OpenRecordLink
                        entity={outcome.entity}
                        domain={outcome.domain}
                        resultRefId={outcome.resultRefId}
                    />
                    <span className="text-muted-foreground text-[10px] tabular-nums">
                        {formatRelativeTime(change.at)}
                    </span>
                </div>
            </div>
            <RecordPreview text={change.detail} />
        </li>
    );
}

/**
 * Everything Phoebe has durably changed in this conversation, grouped by the
 * store it changed.
 *
 * Deliberately separate from the transcript. A change that is already applied
 * needs no answer, so putting it in the conversation costs a turn's worth of
 * attention to report something Phoebe has already said in prose. Drafts are
 * the exception and stay in the chat, because they are addressed to the
 * scheduler and block until answered.
 *
 * Polls until every note settles, so nothing here has to be sized or timed to
 * fit inside a turn.
 */
export function ChangesPanel() {
    // Reads what SavedNoteOutcomesProvider already fetched. Fetching here too
    // would run the identical poll twice against the same endpoint.
    const notes = useSavedNotes();

    const groups = useMemo(() => {
        const byDomain = new Map<AgentNoteItemRead['domain'], Array<Change>>();
        for (const note of notes) {
            for (const outcome of noteOutcomes(note)) {
                // Drafts live in the chat, where they can be acted on.
                if (outcome.tone === 'needs-you') {
                    continue;
                }
                const group = GROUP_FOR_DOMAIN[outcome.domain];
                const existing = byDomain.get(group) ?? [];
                existing.push({
                    outcome,
                    detail: outcome.detail || note.content,
                    at: note.created_at,
                });
                byDomain.set(group, existing);
            }
        }
        return GROUP_ORDER.flatMap((domain) => {
            const changes = byDomain.get(domain);
            if (!changes || changes.length === 0) {
                return [];
            }
            // Newest first inside a group; the group order is fixed so the
            // panel does not reshuffle under the reader between turns.
            return [
                {
                    domain,
                    changes: [...changes].sort((a, b) =>
                        b.at.localeCompare(a.at),
                    ),
                },
            ];
        });
    }, [notes]);

    if (groups.length === 0) {
        return (
            <p className="text-muted-foreground p-4 text-center text-xs">
                Nothing changed in this chat yet.
            </p>
        );
    }

    return (
        <div className="flex flex-col gap-4 p-3">
            {groups.map((group) => (
                <section key={group.domain} className="flex flex-col gap-1.5">
                    <h3 className="text-muted-foreground text-[10px] font-semibold tracking-wide uppercase">
                        {GROUP_LABELS[group.domain]}
                    </h3>
                    <ul className="flex list-none flex-col gap-2">
                        {group.changes.map((change) => (
                            <ChangeRow
                                key={change.outcome.itemId}
                                change={change}
                            />
                        ))}
                    </ul>
                </section>
            ))}
        </div>
    );
}
